import React, { useCallback, useEffect, useState } from "react";

// PROVE 모델 서버(2026-09-29) — 학습한 품목분류 모델을 돌리는 GPU 포드를 관리자가 버튼으로 켜고 끈다.
// 켜기: 조건에 맞는 GPU 를 골라 LoginHS 전용 vast 템플릿으로 포드를 만든다 → 포드가 B2 에서 모델을 받고 vLLM 을 띄운 뒤
// 스스로 주소를 등록한다(5~8분). 끄기: 포드를 삭제해 요금이 멈춘다. 꺼져 있으면 검색 화면의 'PROVE 모델' 줄만 빠진다.
function authHeaders() {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const MUTED = "var(--audit-muted, #6b7482)";
const LINE = "var(--audit-line, #e3e6ea)";
const BLUE = "#1a56db";

function ago(sec) {
  if (sec == null) return "";
  if (sec < 60) return `${sec}초 전`;
  if (sec < 3600) return `${Math.round(sec / 60)}분 전`;
  return `${Math.round(sec / 3600)}시간 전`;
}

export default function AdminHsModel({ API_BASE_URL = "", fetchImpl = fetch, pollMs = 15000 }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetchImpl(`${API_BASE_URL}/api/onto/admin/hs-model/status`, { headers: authHeaders() });
      if (r.status === 401 || r.status === 403) { setError("관리자 로그인이 필요합니다(토큰 만료일 수 있습니다)"); return; }
      const j = await r.json();
      setStatus(j); setError(null);
    } catch (e) {
      setError(String(e?.message || e));
    }
  }, [API_BASE_URL, fetchImpl]);

  useEffect(() => {
    load();
    const t = setInterval(load, pollMs);
    return () => clearInterval(t);
  }, [load, pollMs]);

  const act = async (kind) => {
    if (kind === "stop" && !window.confirm("PROVE 모델 서버를 끄시겠습니까? 포드가 삭제되고 요금이 멈춥니다. 검색 화면의 'PROVE 모델' 줄은 빠집니다.")) return;
    setBusy(kind); setMessage(null);
    try {
      const r = await fetchImpl(`${API_BASE_URL}/api/onto/admin/hs-model/${kind}`, { method: "POST", headers: authHeaders() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) setMessage(j.error || `오류 ${r.status}`);
      else if (kind === "start") setMessage(j.result === "already_running" ? "이미 켜져 있습니다." : `켜는 중입니다 — ${j.offer?.gpu || ""} · ${j.offer?.location || ""} · 시간당 ${j.offer?.price_per_hour ?? "?"}달러. 모델 준비까지 5~8분 걸립니다.`);
      else setMessage(`껐습니다 — 삭제한 포드 ${(j.destroyed || []).length}개.`);
      await load();
    } catch (e) {
      setMessage(String(e?.message || e));
    } finally {
      setBusy(null);
    }
  };

  const pods = status?.pods || [];
  const reg = status?.registered;
  const state = !status ? "…" : status.healthy ? "켜짐 · 응답함" : pods.length ? "켜는 중(모델 준비 중)" : "꺼짐";
  return (
    <div data-testid="admin-hs-model" style={{ maxWidth: 820 }}>
      <h3 style={{ margin: "4px 0 6px" }}>PROVE 모델 서버</h3>
      <p style={{ margin: "0 0 14px", color: MUTED, fontSize: 13, lineHeight: 1.6 }}>
        결정례·관세사 신고이력으로 학습한 품목분류 모델을 돌리는 GPU 서버입니다. 켜 둔 시간만 요금이 나갑니다.
        꺼져 있어도 LoginHS 는 그대로 쓰이고, 검색 화면 출처별 세번의 <b>PROVE 모델</b> 줄만 빠집니다.
      </p>
      {error && <div role="alert" style={{ color: "#b42318", marginBottom: 10 }}>{error}</div>}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
        <span data-testid="hs-model-state" style={{ fontWeight: 700, color: status?.healthy ? BLUE : "inherit" }}>{state}</span>
        <button type="button" data-testid="hs-model-start" disabled={!!busy || !status?.vast_configured || pods.length > 0} onClick={() => act("start")}>
          {busy === "start" ? "켜는 중…" : "켜기"}
        </button>
        <button type="button" data-testid="hs-model-stop" disabled={!!busy || !status?.vast_configured || pods.length === 0} onClick={() => act("stop")}>
          {busy === "stop" ? "끄는 중…" : "끄기"}
        </button>
        <button type="button" onClick={load} disabled={!!busy}>새로고침</button>
      </div>
      {message && <div data-testid="hs-model-message" style={{ marginBottom: 12, fontSize: 13 }}>{message}</div>}
      {status && !status.vast_configured && <div style={{ color: MUTED, fontSize: 13, marginBottom: 12 }}>서버에 vast 설정(VAST_API_KEY·HS_MODEL_VAST_TEMPLATE)이 없어 버튼을 쓸 수 없습니다.</div>}
      <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
        <tbody>
          <tr><td style={{ padding: "6px 8px", borderBottom: `1px solid ${LINE}`, color: MUTED, width: 150 }}>쓰는 주소</td>
            <td style={{ padding: "6px 8px", borderBottom: `1px solid ${LINE}` }}>{status?.using_url || "없음"} {status?.using_source === "registered" ? "(포드가 등록)" : status?.using_source === "configured" ? "(서버 설정값)" : ""}</td></tr>
          <tr><td style={{ padding: "6px 8px", borderBottom: `1px solid ${LINE}`, color: MUTED }}>마지막 등록</td>
            <td style={{ padding: "6px 8px", borderBottom: `1px solid ${LINE}` }}>{reg ? `${ago(reg.age_sec)} · ${reg.gpu || ""} · 모델 ${reg.model || ""}` : "없음"}</td></tr>
          {pods.map((p) => (
            <tr key={p.id}><td style={{ padding: "6px 8px", borderBottom: `1px solid ${LINE}`, color: MUTED }}>포드 {p.id}</td>
              <td style={{ padding: "6px 8px", borderBottom: `1px solid ${LINE}` }}>{p.status} · {p.gpu} {p.gpu_ram_gb}GB · {p.location} · 시간당 {p.price_per_hour}달러{p.status_msg ? ` · ${String(p.status_msg).slice(0, 60)}` : ""}</td></tr>
          ))}
          <tr><td style={{ padding: "6px 8px", color: MUTED }}>고르는 조건</td>
            <td style={{ padding: "6px 8px", color: MUTED }}>GPU 1장 · 메모리 {status?.min_gpu_ram_gb ?? 80}GB 이상 · 시간당 {status?.max_price_per_hour ?? 2.5}달러 이하 · 아시아 먼저, 그다음 싼 순</td></tr>
        </tbody>
      </table>
      {status?.vast_error && <div style={{ color: MUTED, fontSize: 12, marginTop: 8 }}>vast 조회 오류: {status.vast_error}</div>}
      <NightlyChecks API_BASE_URL={API_BASE_URL} fetchImpl={fetchImpl} />
      <AnswerStoreStats API_BASE_URL={API_BASE_URL} fetchImpl={fetchImpl} />
      <HistoryCheckStats API_BASE_URL={API_BASE_URL} fetchImpl={fetchImpl} />
    </div>
  );
}

const SET_LABEL = { precedent: "결정례", express: "특송", general: "일반 신고" };

/** 같은 시각의 묶음별 줄을 한 회차로 모은다(최근 순). */
export function groupRuns(rows) {
  const runs = [];
  const byAt = new Map();
  for (const r of rows || []) {
    let run = byAt.get(r.run_at);
    if (!run) { run = { at: r.run_at, sets: {}, note: r.note || null, model: r.model_dir || null }; byAt.set(r.run_at, run); runs.push(run); }
    run.sets[r.set_name] = r;
    if (r.note) run.note = r.note;
  }
  return runs;
}

// 매일 품질 점검(2026-10-05) — 매일 02시 결정례·특송·일반 각 300건을 캐시 없이 물어 정확도·응답 시간을 남긴다. 떨어지면 메일.
function NightlyChecks({ API_BASE_URL, fetchImpl }) {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await fetchImpl(`${API_BASE_URL}/api/onto/admin/hs-model/nightly`, { headers: authHeaders() });
      if (r.ok) setData(await r.json());
    } catch (_) { /* 표만 비운다 */ }
  }, [API_BASE_URL, fetchImpl]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data?.running) return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [data?.running, load]);
  const runNow = async () => {
    setMsg(null);
    const r = await fetchImpl(`${API_BASE_URL}/api/onto/admin/hs-model/nightly/run`, { method: "POST", headers: authHeaders() });
    const j = await r.json().catch(() => ({}));
    setMsg(r.ok ? "점검을 시작했습니다 — 2분쯤 걸립니다." : (j.error || `오류 ${r.status}`));
    load();
  };
  if (!data?.available) return null;
  const runs = groupRuns(data.rows).slice(0, 30);
  const cell = { padding: "5px 8px", borderBottom: `1px solid ${LINE}`, textAlign: "right" };
  const head = { ...cell, color: MUTED, fontWeight: 500 };
  return (
    <section data-testid="hs-model-nightly" style={{ marginTop: 24 }}>
      <h4 style={{ margin: "0 0 4px" }}>매일 품질 점검</h4>
      <p style={{ margin: "0 0 10px", color: MUTED, fontSize: 13, lineHeight: 1.6 }}>
        매일 새벽 2시에 결정례·특송·일반 신고 각 300건을 지금 모델에 물어 맞힌 비율(10자리)과 응답 시간을 남깁니다.
        모델 서버가 꺼져 있거나, 오류가 5% 넘거나, 10자리가 지난 7회보다 3%p 넘게 떨어지면 메일로 알립니다.
      </p>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
        <button type="button" onClick={runNow} disabled={data.running}>{data.running ? "점검 중…" : "지금 점검"}</button>
        {msg && <span style={{ fontSize: 13 }}>{msg}</span>}
      </div>
      <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
        <thead><tr>
          <th style={{ ...head, textAlign: "left" }}>회차</th>
          {Object.keys(SET_LABEL).map((k) => <th key={k} style={head}>{SET_LABEL[k]} 10자리</th>)}
          <th style={head}>평균 응답</th><th style={{ ...head, textAlign: "left" }}>메모</th>
        </tr></thead>
        <tbody>
          {runs.map((run) => {
            const ms = Object.values(run.sets).map((r) => r.avg_ms).filter((v) => v != null);
            return (
              <tr key={run.at}>
                <td style={{ ...cell, textAlign: "left" }}>{String(run.at).replace("T", " ").slice(0, 16)}</td>
                {Object.keys(SET_LABEL).map((k) => {
                  const r = run.sets[k];
                  return <td key={k} style={cell} title={r ? `호 ${r.hs4} · 소호 ${r.hs6} · 오류 ${r.errors}/${r.n}` : ""}>{r ? `${Number(r.hsk).toFixed(1)}%` : "—"}</td>;
                })}
                <td style={cell}>{ms.length ? `${(ms.reduce((a, b) => a + b, 0) / ms.length / 1000).toFixed(2)}초` : "—"}</td>
                <td style={{ ...cell, textAlign: "left", color: run.note ? "#1f2937" : MUTED }}>{run.note || "정상"}</td>
              </tr>
            );
          })}
          {!runs.length && <tr><td colSpan={6} style={{ ...cell, textAlign: "left", color: MUTED }}>아직 기록이 없습니다.</td></tr>}
        </tbody>
      </table>
    </section>
  );
}

/** 하루 집계 한 줄의 반복 비율 — 메모리·저장소에서 바로 답한 몫(모델을 부르지 않은 몫). */
export function reuseRate(d) {
  const req = Number(d?.requests || 0);
  if (!req) return null;
  return Math.round((1000 * (Number(d.memory_hits || 0) + Number(d.store_hits || 0))) / req) / 10;
}

// 답 저장소(2026-10-05) — 같은 물품 글은 모델별로 한 번만 계산한다(고정 거래처 반복 품목). 모델이 바뀌면 02:40 새 모델로 다시 묻는다.
function AnswerStoreStats({ API_BASE_URL, fetchImpl }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetchImpl(`${API_BASE_URL}/api/onto/admin/hs-model/answer-store`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null));
  }, [API_BASE_URL, fetchImpl]);
  if (!data?.available) return null;
  const st = data.stats || {};
  const days = st.days || [];
  const cell = { padding: "5px 8px", borderBottom: `1px solid ${LINE}`, textAlign: "right" };
  const head = { ...cell, color: MUTED, fontWeight: 500 };
  return (
    <section data-testid="hs-model-answer-store" style={{ marginTop: 24 }}>
      <h4 style={{ margin: "0 0 4px" }}>답 저장소</h4>
      <p style={{ margin: "0 0 10px", color: MUTED, fontSize: 13, lineHeight: 1.6 }}>
        모델이 한 번 답한 물품 글은 모델별로 남겨, 같은 글(고정 거래처의 같은 품목)이 다시 오면 모델을 부르지 않고 바로·같은 세번으로 답합니다.
        저장 품목 <b>{Number(st.texts || 0).toLocaleString()}</b>개 · 이틀 이상 다시 온 품목 <b>{Number(st.seen_two_days_or_more || 0).toLocaleString()}</b>개
        · 저장소에서 답한 횟수 {Number(st.total_hits || 0).toLocaleString()}회
        {data.refresh?.started_at && <> · 마지막 새로 묻기 {String(data.refresh.started_at).slice(0, 16).replace("T", " ")} ({data.refresh.saved ?? 0}건)</>}
      </p>
      <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
        <thead><tr>
          <th style={{ ...head, textAlign: "left" }}>날짜</th><th style={head}>요청</th><th style={head}>메모리</th>
          <th style={head}>저장소</th><th style={head}>모델 계산</th><th style={head}>바로 답한 비율</th>
        </tr></thead>
        <tbody>
          {days.map((d) => (
            <tr key={d.day}>
              <td style={{ ...cell, textAlign: "left" }}>{d.day}</td>
              <td style={cell}>{Number(d.requests).toLocaleString()}</td>
              <td style={cell}>{Number(d.memory_hits).toLocaleString()}</td>
              <td style={cell}>{Number(d.store_hits).toLocaleString()}</td>
              <td style={cell}>{Number(d.model_calls).toLocaleString()}</td>
              <td style={cell}>{reuseRate(d) == null ? "—" : `${reuseRate(d)}%`}</td>
            </tr>
          ))}
          {!days.length && <tr><td colSpan={6} style={{ ...cell, textAlign: "left", color: MUTED }}>아직 집계가 없습니다(5분마다 모읍니다).</td></tr>}
        </tbody>
      </table>
    </section>
  );
}

/** 색인 이름 → 화면 이름(관세사 출처는 익명 — 사무소 번호·관세사 이름을 보이지 않는다). */
export function sourceLabel(idx, i) {
  const s = String(idx || "");
  if (s.startsWith("dhl-")) return "특송 B";
  if (s.startsWith("star-")) return "특송 A";
  if (s.includes("-1033-")) return "특송 C";
  return `일반 통관 ${i + 1}`;
}

const pct = (a, b) => (b ? `${(Math.round((1000 * a) / b) / 10).toFixed(1)}%` : "—");

// 관세사 신고이력 대조(2026-10-05) — 최근 1년·2회 이상 신고된 품목을 모델에 물어 신고 세번과 견준다. 다른 것이 학습·사후검증 후보.
function HistoryCheckStats({ API_BASE_URL, fetchImpl }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetchImpl(`${API_BASE_URL}/api/onto/admin/hs-model/history-check`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null));
  }, [API_BASE_URL, fetchImpl]);
  if (!data?.available) return null;
  const st = data.stats || {};
  const rows = st.by_index || [];
  let general = 0;
  const cell = { padding: "5px 8px", borderBottom: `1px solid ${LINE}`, textAlign: "right" };
  const head = { ...cell, color: MUTED, fontWeight: 500 };
  const total = rows.reduce((a, r) => a + Number(r.checked || 0), 0);
  return (
    <section data-testid="hs-model-history-check" style={{ marginTop: 24 }}>
      <h4 style={{ margin: "0 0 4px" }}>관세사 신고이력 대조</h4>
      <p style={{ margin: "0 0 10px", color: MUTED, fontSize: 13, lineHeight: 1.6 }}>
        매일 새벽 2:50~6:00 최근 1년 안에 2번 이상 신고된 품목을 모델에 물어 신고 세번과 견줍니다(밤마다 이어서).
        다른 것은 다음 학습의 어려운 사례·사후검증 후보입니다. 모델은 이 이력 일부로 학습했으므로 같다는 것이 맞았다는 증거는 아닙니다.
        {" "}대조 {total.toLocaleString()}건{st.running ? " · 지금 대조 중" : ""}
        {st.last?.started_at && <> · 마지막 실행 {String(st.last.started_at).slice(0, 16).replace("T", " ")} ({Number(st.last.saved || 0).toLocaleString()}건, {st.last.stop})</>}
      </p>
      <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
        <thead><tr>
          <th style={{ ...head, textAlign: "left" }}>출처</th><th style={head}>대조</th><th style={head}>호 같음</th>
          <th style={head}>소호 같음</th><th style={head}>10자리 같음</th><th style={head}>진행</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => {
            const label = String(r.idx).startsWith("office-") && !String(r.idx).includes("-1033-") ? sourceLabel(r.idx, general++) : sourceLabel(r.idx, 0);
            const n = Number(r.checked || 0);
            return (
              <tr key={r.idx}>
                <td style={{ ...cell, textAlign: "left" }}>{label}</td>
                <td style={cell}>{n.toLocaleString()}</td>
                <td style={cell}>{pct(Number(r.same4), n)}</td>
                <td style={cell}>{pct(Number(r.same6), n)}</td>
                <td style={cell}>{pct(Number(r.same10), n)}</td>
                <td style={cell}>{r.done ? "끝" : "진행 중"}</td>
              </tr>
            );
          })}
          {!rows.length && <tr><td colSpan={6} style={{ ...cell, textAlign: "left", color: MUTED }}>아직 대조한 것이 없습니다.</td></tr>}
        </tbody>
      </table>
    </section>
  );
}
